/**
 * Every version of a workspace (§5.25): who published it, from which machine or
 * from this page, when, with what message — and what changed, unfolded on
 * demand as readable paths.
 *
 * The history never moves backwards. *Revert to this version* publishes a new
 * version whose document is the old one, so the revert is itself a line here
 * with its own author. It is the owner's and the administrators', and it says
 * before it happens the one thing it cannot do: **secrets are not versioned**.
 * A revert brings back the names of the secrets that version had, holding the
 * values they hold today.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import { color, font } from '../../theme/tokens';
import { Badge, Callout, Card, Empty, GhostButton, Row, RowAction, Table } from '../../components/primitives';
import { ChevronRight } from '../../components/icons';
import { useConfirm } from '../../components/Modal';
import { api, ifMatch, apiPath, prefix } from '../../lib/api';
import type {
  DiffResponse,
  Me,
  WorkspaceRecord,
  PublishConflict,
  RevisionDetail,
  RevisionDiff,
  RevisionList,
  RevisionSummary
} from '../../lib/types';
import { DiffView } from './DiffView';
import { actorLabel, administers, conflictOf, failureText, selectStyle } from './shared';
import { DateCell } from '../../components/DateCell';

export function WorkspaceHistory({
  me,
  orgId,
  workspaceId,
  focus
}: {
  me: Me;
  orgId: string;
  workspaceId: string;
  /** A version to open unfolded — the audit page links straight to one. */
  focus: number | null;
}): React.JSX.Element {
  const [dialog, ask] = useConfirm();
  const [record, setRecord] = useState<WorkspaceRecord | null>(null);
  const [revisions, setRevisions] = useState<RevisionSummary[] | null>(null);
  const [open, setOpen] = useState<Record<number, RevisionDetail | 'loading'>>({});
  const [from, setFrom] = useState<number | null>(null);
  const [to, setTo] = useState<number | null>(null);
  const [compared, setCompared] = useState<RevisionDiff | null>(null);
  const [comparedPair, setComparedPair] = useState<[number, number] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const base = apiPath`/orgs/${orgId}/workspaces/${workspaceId}`;
  const admin = administers(me, orgId);
  const columns = '40px 76px minmax(0,1.3fr) minmax(0,1fr) 140px 76px 120px';

  const expand = useCallback(
    async (version: number, force = false): Promise<void> => {
      if (!force && open[version]) {
        setOpen(({ [version]: _closed, ...rest }) => rest);
        return;
      }
      setOpen((current) => ({ ...current, [version]: 'loading' }));
      try {
        const detail = await api.get<RevisionDetail>(apiPath`${prefix(base)}/revisions/${version}`);
        setOpen((current) => ({ ...current, [version]: detail }));
      } catch (failure) {
        setOpen(({ [version]: _failed, ...rest }) => rest);
        setError(failureText(failure));
      }
    },
    [base, open]
  );

  const load = useCallback(async (): Promise<void> => {
    const [workspace, list] = await Promise.all([api.get<WorkspaceRecord>(base), api.get<RevisionList>(apiPath`${prefix(base)}/revisions`)]);
    setRecord(workspace);
    setRevisions(list.revisions);
    const newest = list.revisions[0]?.version ?? null;
    setTo((current) => current ?? newest);
    setFrom((current) => current ?? list.revisions[1]?.version ?? newest);
  }, [base]);

  useEffect(() => {
    load()
      .then(() => {
        if (focus !== null) void expand(focus, true);
      })
      .catch((failure: unknown) => setError(failureText(failure, 'The history could not be read.')));
    // The focus is read once, on arrival.
  }, [load]);

  const compare = async (): Promise<void> => {
    if (from === null || to === null) return;
    setError(null);
    try {
      const answer = await api.get<DiffResponse>(apiPath`${prefix(base)}/diff?from=${from}&to=${to}`);
      setCompared(answer.diff);
      setComparedPair([from, to]);
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  const revert = async (version: number): Promise<void> => {
    if (!record) return;
    const next = record.workspace.version + 1;
    const agreed = await ask({
      title: `Revert to version ${version}?`,
      lede:
        `This publishes version ${next}, whose document is version ${version}’s. Secrets are not versioned: the ones ` +
        `version ${version} named come back by name, holding the values they hold now — a value replaced since is not restored.`,
      confirm: `Publish version ${next}`
    });
    if (!agreed) return;
    setBusy(version);
    setError(null);
    try {
      await api.send('POST', apiPath`${prefix(base)}/restore/${version}`, {}, ifMatch(record.workspace.version));
      setOpen({});
      setCompared(null);
      await load();
    } catch (failure) {
      if (conflictOf<PublishConflict>(failure)) {
        setError('Somebody published while this page was open. The history is reloaded — look again, then revert.');
        await load().catch(() => undefined);
      } else setError(failureText(failure));
    } finally {
      setBusy(null);
    }
  };

  if (revisions === null) {
    return error ? (
      <Empty title="The history could not be read">{error}</Empty>
    ) : (
      <Empty title="Reading the history…" />
    );
  }

  const current = record?.workspace.version ?? revisions[0]?.version ?? 0;
  const same = from === null || to === null || from === to;

  return (
    <>
      {dialog}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <span style={{ fontSize: 18, fontWeight: 600 }}>{record?.workspace.name ?? 'Workspace'}</span>
        <Badge tone="off">v{current}</Badge>
      </div>

      <Table columns={columns} header={['', 'Version', 'Message', 'By', 'When', 'Changes', '']}>
        {revisions.map((revision) => {
          const detail = open[revision.version];
          return (
            <div key={revision.version} style={{ background: detail ? 'rgba(0,0,0,.035)' : 'transparent' }}>
              {/* A row that unfolds (§25): the chevron is the keyboard's way in,
                  and the whole row opens and closes under the pointer. */}
              <div
                onClick={(event) => {
                  if ((event.target as HTMLElement).closest('button') === null) void expand(revision.version);
                }}
                style={{ cursor: 'pointer' }}
              >
                <Row columns={columns}>
                  <button
                    type="button"
                    aria-expanded={Boolean(detail)}
                    aria-controls={`revision-${revision.version}`}
                    aria-label={`Details for v${revision.version}`}
                    onClick={() => void expand(revision.version)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 28,
                      height: 28,
                      padding: 0,
                      border: 0,
                      borderRadius: 6,
                      background: 'none',
                      color: color.inkSubtle,
                      cursor: 'pointer'
                    }}
                  >
                    <span style={{ display: 'flex', transform: detail ? 'rotate(90deg)' : 'none', transition: 'transform 150ms ease' }}>
                      <ChevronRight size={12} />
                    </span>
                  </button>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontFamily: font.mono, fontSize: 12.5 }}>v{revision.version}</span>
                    {revision.version === current && <Badge tone="ok">now</Badge>}
                  </span>
                  <span style={{ color: revision.message ? color.inkBody : color.inkFaint, minWidth: 0 }}>
                    {revision.message ?? '—'}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div>{actorLabel(revision.author)}</div>
                    <div style={{ fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint, marginTop: 3 }}>
                      {revision.device ? (revision.device.label ?? 'a machine') : 'browser'}
                    </div>
                  </div>
                  <DateCell at={revision.at} style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkSubtle }} />
                  <span style={{ fontFamily: font.mono, fontSize: 12.5, textAlign: 'right' }}>{revision.changes}</span>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {admin && revision.version !== current && (
                      <RowAction onClick={() => void revert(revision.version)}>
                        {busy === revision.version ? 'Reverting' : 'Revert to this'}
                      </RowAction>
                    )}
                  </div>
                </Row>
              </div>
              {detail && (
                <div id={`revision-${revision.version}`} style={{ padding: '8px 16px 20px 52px', borderBottom: `1px solid ${color.line}` }}>
                  {detail === 'loading' ? (
                    <span style={{ fontSize: 13, color: color.inkFaint }}>Reading version {revision.version}…</span>
                  ) : (
                    <DiffView diff={detail.diff} empty="This version changed nothing that a diff shows — a message, or a secret’s value." />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Table>
      {error && <p style={{ fontSize: 13.5, color: color.badText, margin: '16px 0 0' }}>{error}</p>}

      {revisions.length > 1 && (
        <Card style={{ marginTop: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Compare two versions</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 14 }}>
            <VersionSelect label="From" value={from} revisions={revisions} onChange={setFrom} />
            <VersionSelect label="To" value={to} revisions={revisions} onChange={setTo} />
            <span title={same ? 'Pick two different versions' : undefined} style={{ display: 'inline-flex' }}>
              <GhostButton size="sm" onClick={() => void compare()} disabled={same}>
                Compare
              </GhostButton>
            </span>
          </div>
          {compared && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.1em', textTransform: 'uppercase', color: color.inkFaint, marginBottom: 10 }}>
                {comparedPair ? `v${comparedPair[0]} → v${comparedPair[1]} · ` : ''}
                {compared.length} change{compared.length === 1 ? '' : 's'}
              </div>
              <DiffView diff={compared} empty="Those two versions hold the same document." />
            </div>
          )}
        </Card>
      )}

      <div style={{ maxWidth: 760 }}>
        <Callout tone="amber" title="Secrets are not versioned">
          A revision records which secrets a workspace named, never their values. Reverting brings the names back with the
          values the secrets hold today; a value somebody replaced is gone, and the audit says who replaced it and when.
        </Callout>
      </div>
    </>
  );
}

function VersionSelect({
  label,
  value,
  revisions,
  onChange
}: {
  label: string;
  value: number | null;
  revisions: RevisionSummary[];
  onChange: (version: number) => void;
}): React.JSX.Element {
  return (
    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: color.inkMuted }}>
      {label}
      <select
        value={value ?? ''}
        onChange={(event) => onChange(Number(event.target.value))}
        style={{ ...selectStyle, width: 96, fontFamily: font.mono, fontSize: 13, padding: '6px 10px' }}
      >
        {revisions.map((revision) => (
          <option key={revision.version} value={revision.version}>
            v{revision.version}
          </option>
        ))}
      </select>
    </label>
  );
}
